import { Navbar, Footer } from "@/components/site-chrome";
import { Button } from "@/components/ui/button";
import { Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { Store, Smartphone, TrendingUp, Shield, ArrowRight, CheckCircle2 } from "lucide-react";
import {
  CreamBackground, DarkBackground, WineBackground,
  SectionLabel, Reveal, DisplayHeadline, SandCorner,
  WINE, CREAM, CREAM2, CHARCOAL, SAND, FONT_DISPLAY, FONT_SANS,
} from "@/components/landing/editorial-backgrounds";

const features = [
  { icon: Store,       title: "Accept payments on campus", desc: "Canteen, bookshop, uniform shop, clinic — every campus merchant on one rail." },
  { icon: Smartphone,  title: "Tap-to-pay or QR",          desc: "Students pay with a tap of their SchoolOS card or a QR scan. No cash handling." },
  { icon: TrendingUp,  title: "Daily settlement",          desc: "Funds settle to your bank account daily. See every transaction in real time." },
  { icon: Shield,      title: "Fraud-proof",              desc: "Every transaction carries a unique WTX. Disputes resolve in seconds, not days." },
];

const benefits = [
  "Zero cash handling — eliminate theft, shortages and counting time",
  "Student wallet payments with instant confirmation",
  "POS interface optimized for speed — serve more students per break",
  "Inventory tracking with real-time stock alerts",
  "Daily bank settlement with full transaction breakdown",
  "Lowest transaction fees — one consolidated rail, not multiple",
];

export function MerchantsPage() {
  return (
    <CreamBackground>
      <Navbar />

      {/* ── HERO ── */}
      <section className="mx-auto max-w-7xl px-6 pb-20 pt-16 sm:px-8 sm:pt-24">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <div>
            <SectionLabel label="For Merchants" />
            <DisplayHeadline top="Every campus merchant." bottom="One rail." />
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-lg text-base leading-relaxed sm:text-lg" style={{ fontFamily: FONT_DISPLAY, fontStyle: "italic", color: `${CHARCOAL}80` }}>
                Canteen, bookshop, uniform shop, clinic — accept student wallet payments
                instantly with zero cash handling. Daily settlement to your bank account.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="mt-10 flex flex-wrap gap-3">
                <Button asChild size="lg" className="h-12 rounded-none px-8 text-[13px] font-bold uppercase tracking-[0.15em] text-white hover:opacity-90" style={{ background: WINE, border: "none", fontFamily: FONT_SANS }}>
                  <Link to="/auth">Get Started <ArrowRight className="ml-2 h-4 w-4" /></Link>
                </Button>
                <Button asChild variant="ghost" size="lg" className="h-12 rounded-none px-7 text-[13px] font-bold uppercase tracking-[0.15em] hover:bg-transparent" style={{ border: `1.5px solid ${CHARCOAL}30`, color: CHARCOAL, fontFamily: FONT_SANS }}>
                  <Link to="/auth">Sign In</Link>
                </Button>
              </div>
            </Reveal>
          </div>

          {/* POS terminal mockup — editorial card */}
          <Reveal delay={0.25}>
            <div className="relative overflow-hidden p-8" style={{ background: CHARCOAL, border: `1.5px solid ${CHARCOAL}20`, boxShadow: "0 24px 64px -28px rgba(123,20,22,0.20)" }}>
              {/* Warm glow */}
              <div className="pointer-events-none absolute inset-0" style={{ background: `radial-gradient(ellipse 60% 40% at 30% 20%, ${WINE}18 0%, transparent 50%)` }} />
              <div className="relative">
                <div className="mb-4 flex items-center gap-2 text-sm" style={{ color: "rgba(255,255,255,0.6)", fontFamily: FONT_SANS }}>
                  <Smartphone className="h-4 w-4" /> POS Terminal
                </div>
                <div className="space-y-3">
                  <div className="p-4" style={{ background: "rgba(255,255,255,0.06)", border: `1px solid ${SAND}20` }}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm" style={{ color: "rgba(255,255,255,0.7)", fontFamily: FONT_SANS }}>Customer</span>
                      <span className="font-medium text-white" style={{ fontFamily: FONT_SANS }}>Sarah Namusoke</span>
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <span className="text-sm" style={{ color: "rgba(255,255,255,0.7)", fontFamily: FONT_SANS }}>Items</span>
                      <span className="text-sm text-white" style={{ fontFamily: FONT_SANS }}>Lunch · Snack · Juice</span>
                    </div>
                    <div className="mt-3 flex items-center justify-between border-t pt-3" style={{ borderColor: "rgba(255,255,255,0.15)" }}>
                      <span className="text-lg" style={{ fontFamily: FONT_DISPLAY, color: "rgba(255,255,255,0.9)" }}>Total</span>
                      <span className="text-xl" style={{ fontFamily: FONT_DISPLAY, color: SAND }}>UGX 8,500</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 px-4 py-2.5 text-sm" style={{ background: `${SAND}15`, color: SAND, fontFamily: FONT_SANS }}>
                    <CheckCircle2 className="h-4 w-4" /> Paid · WTX-a3f8b2
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section className="mx-auto max-w-7xl px-6 py-20 sm:px-8">
        <SectionLabel label="Capabilities" />
        <div className="mt-12 grid gap-px md:grid-cols-2 lg:grid-cols-4" style={{ border: `1.5px solid ${CHARCOAL}15` }}>
          {features.map((f, i) => (
            <Reveal key={f.title} delay={i * 0.08}>
              <div className="h-full p-8" style={{ background: i % 2 === 0 ? CREAM : CREAM2, borderRight: i < 3 ? `1px solid ${CHARCOAL}10` : "none" }}>
                <div className="flex h-12 w-12 items-center justify-center" style={{ border: `1.5px solid ${WINE}30`, background: `${WINE}08` }}>
                  <f.icon className="h-5 w-5" style={{ color: WINE }} />
                </div>
                <h3 className="mt-5 text-lg font-bold" style={{ color: CHARCOAL, fontFamily: FONT_DISPLAY }}>{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed" style={{ color: `${CHARCOAL}70`, fontFamily: FONT_SANS }}>{f.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ── BENEFITS — dark section ── */}
      <DarkBackground>
        <section className="mx-auto max-w-5xl px-6 py-24 sm:px-8">
          <div className="flex items-center justify-center gap-4">
            <div className="h-px w-12" style={{ background: "rgba(255,255,255,0.25)" }} />
            <span className="text-[10px] font-bold uppercase tracking-[0.3em]" style={{ color: "rgba(255,255,255,0.55)", fontFamily: FONT_SANS }}>Why Merchants Switch</span>
            <div className="h-px w-12" style={{ background: "rgba(255,255,255,0.25)" }} />
          </div>
          <Reveal delay={0.1}>
            <h2 className="mt-8 text-center text-white" style={{ fontFamily: FONT_DISPLAY, lineHeight: 0.95, letterSpacing: "-0.03em", fontSize: "clamp(2rem, 5vw, 4rem)", fontWeight: 700 }}>
              Stop counting cash.
            </h2>
            <p className="text-center" style={{ fontFamily: FONT_DISPLAY, color: SAND, lineHeight: 0.95, letterSpacing: "-0.03em", fontSize: "clamp(2rem, 5vw, 4rem)", fontWeight: 700, fontStyle: "italic" }}>
              Start counting seconds.
            </p>
          </Reveal>
          <div className="mt-14 grid gap-4 sm:grid-cols-2">
            {benefits.map((item, i) => (
              <Reveal key={item} delay={i * 0.06}>
                <div className="flex items-start gap-3 p-4" style={{ border: `1px solid ${SAND}20`, background: `${SAND}08` }}>
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" style={{ color: SAND }} />
                  <span className="text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.85)", fontFamily: FONT_SANS }}>{item}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      </DarkBackground>

      {/* ── CTA ── */}
      <WineBackground>
        <section className="mx-auto max-w-3xl px-6 py-28 text-center sm:py-36">
          <div className="relative p-12 sm:p-16" style={{ border: `1.5px solid ${SAND}40` }}>
            <SandCorner position="tl" />
            <SandCorner position="tr" />
            <SandCorner position="bl" />
            <SandCorner position="br" />
            <Reveal>
              <h2 className="text-white" style={{ fontFamily: FONT_DISPLAY, lineHeight: 0.92, letterSpacing: "-0.03em", fontSize: "clamp(2.5rem, 7vw, 5rem)", fontWeight: 700 }}>
                Serve more students.
              </h2>
              <p style={{ fontFamily: FONT_DISPLAY, color: SAND, lineHeight: 0.92, letterSpacing: "-0.03em", fontSize: "clamp(2.5rem, 7vw, 5rem)", fontWeight: 700, fontStyle: "italic" }}>
                Handle less cash.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mx-auto mt-8 max-w-md text-base leading-relaxed" style={{ fontFamily: FONT_DISPLAY, fontStyle: "italic", color: "rgba(255,255,255,0.65)" }}>
                Join the campus payment rail. Onboard in minutes.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <Button asChild size="lg" className="mt-10 h-12 rounded-none px-8 text-[13px] font-bold uppercase tracking-[0.15em] hover:opacity-90" style={{ background: SAND, color: CHARCOAL, border: "none", fontFamily: FONT_SANS }}>
                <Link to="/auth">Get Started <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            </Reveal>
          </div>
        </section>
      </WineBackground>

      <Footer />
    </CreamBackground>
  );
}
